import "./ModalImportFromCanva.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import type {
	CanvaAssetType,
	CanvaDateFilter,
	CanvaDesignDto,
	CanvaDesignSortBy,
	CanvaFolderBreadcrumb,
	CanvaFolderDto,
	CanvaImportItemDto,
	CanvaImportStatusDto,
} from "shared";
import { CANVA_MAX_BATCH_SIZE } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { Dialog, useDialog } from "../common/dialog/Dialog";
import { parseError } from "../common/error";
import { Icon, IconSVG } from "../common/Icon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Spinner } from "../common/Spinner";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { SegmentedControl } from "../components/SegmentedControl";
import { LinkGen, QueryKeys } from "../LinkGen";
import { useCanvaQuery } from "../settings/useIntegrations";
import { useCanvaImportStream } from "./useCanvaImportStream";

type AssetFilter = CanvaAssetType | "all";

function isCanvaAuthError(error: unknown): boolean {
	const message = parseError(error)?.toLowerCase() ?? "";
	return (
		message.includes("authorization expired") ||
		message.includes("not connected") ||
		message.includes("please reconnect")
	);
}

const ASSET_FILTERS: Array<{ value: AssetFilter; label: string }> = [
	{ value: "all", label: "All" },
	{ value: "image", label: "Images" },
	{ value: "video", label: "Videos" },
	{ value: "animation", label: "Animations" },
];

const DATE_FILTERS: Array<{ value: CanvaDateFilter; label: string }> = [
	{ value: "all", label: "All time" },
	{ value: "last7", label: "Last 7 days" },
	{ value: "last30", label: "Last 30 days" },
	{ value: "last90", label: "Last 90 days" },
];

const SORT_OPTIONS: Array<{ value: CanvaDesignSortBy; label: string }> = [
	{ value: "modified_descending", label: "Recently modified" },
	{ value: "modified_ascending", label: "Oldest modified" },
	{ value: "title_ascending", label: "Title A–Z" },
	{ value: "title_descending", label: "Title Z–A" },
];

const CANVA_ROOT_FOLDER_ID = "root";

function FolderCard(props: {
	folder: CanvaFolderDto;
	onOpen: (folderId: string) => void;
}) {
	const { folder, onOpen } = props;
	return (
		<button
			type="button"
			className="canva-folder-card"
			onClick={() => onOpen(folder.id)}
		>
			<div className="canva-folder-thumb">
				{folder.thumbnailUrl ? (
					<img src={folder.thumbnailUrl} alt="" loading="lazy" />
				) : (
					<div className="canva-folder-thumb-placeholder">📁</div>
				)}
			</div>
			<div className="canva-design-title" title={folder.name}>
				{folder.name}
			</div>
		</button>
	);
}

function DesignCard(props: {
	design: CanvaDesignDto;
	selected: boolean;
	onToggle: (design: CanvaDesignDto) => void;
}) {
	const { design, selected, onToggle } = props;

	return (
		<button
			type="button"
			className={`canva-design-card${selected ? " selected" : ""}`}
			onClick={() => onToggle(design)}
			aria-pressed={selected}
		>
			<div className="canva-design-thumb">
				{design.thumbnailUrl ? (
					<img src={design.thumbnailUrl} alt="" loading="lazy" />
				) : (
					<div className="canva-design-thumb-placeholder">No preview</div>
				)}
				{design.alreadyImported && (
					<span className="canva-design-imported-badge">Imported</span>
				)}
				{selected && <span className="canva-design-check">✓</span>}
			</div>
			<div className="canva-design-meta">
				<div className="canva-design-title" title={design.title}>
					{design.title}
				</div>
				<div className="canva-design-date">
					{new Date(design.updatedAt).toLocaleDateString()}
				</div>
			</div>
		</button>
	);
}

function ImportProgressPanel(props: {
	status: CanvaImportStatusDto;
	onRetry: (item: CanvaImportItemDto) => void;
	retryingId: number | null;
}) {
	const { status, onRetry, retryingId } = props;
	const finished = status.succeeded + status.failed;
	const progressPercent =
		status.totalAssets > 0
			? Math.round((finished / status.totalAssets) * 100)
			: 0;

	return (
		<div className="canva-import-progress">
			<div className="canva-import-progress-header">
				<strong>
					Importing {finished} / {status.totalAssets}
				</strong>
				<span className="text-muted">
					{status.status === "completed" || status.status === "failed"
						? "Complete"
						: "In progress…"}
				</span>
			</div>
			<div
				className="canva-import-progress-bar"
				role="progressbar"
				aria-valuenow={progressPercent}
				aria-valuemin={0}
				aria-valuemax={100}
			>
				<div
					className="canva-import-progress-bar-fill"
					style={{ width: `${progressPercent}%` }}
				/>
			</div>
			<ul className="canva-import-progress-list">
				{status.items.map((item) => (
					<li
						key={item.id}
						className={`canva-import-item status-${item.status}`}
					>
						<span className="canva-import-item-title" title={item.title}>
							{item.title}
						</span>
						<span className="canva-import-item-status">
							{item.status === "pending" && (item.errorMessage ?? "Pending")}
							{item.status === "in_progress" && "Exporting…"}
							{item.status === "done" && "Done"}
							{item.status === "failed" && (item.errorMessage ?? "Failed")}
						</span>
						{item.status === "failed" && (
							<Button
								size="sm"
								variant="secondary"
								loading={retryingId === item.id}
								onClick={() => onRetry(item)}
							>
								Retry
							</Button>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}

function FolderBreadcrumb(props: {
	path: CanvaFolderBreadcrumb[];
	onNavigate: (folderId: string) => void;
}) {
	if (props.path.length <= 1) {
		return null;
	}

	return (
		<nav className="canva-folder-breadcrumb" aria-label="Canva folders">
			{props.path.map((crumb, index) => (
				<span key={crumb.id} className="canva-folder-crumb">
					{index > 0 && <span className="canva-folder-crumb-sep">/</span>}
					<button
						type="button"
						className={
							index === props.path.length - 1
								? "canva-folder-crumb-current"
								: undefined
						}
						disabled={index === props.path.length - 1}
						onClick={() => props.onNavigate(crumb.id)}
					>
						{crumb.name}
					</button>
				</span>
			))}
		</nav>
	);
}

export function ModalImportFromCanva(props: { folderId?: number | null }) {
	const dialog = useDialog();
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const { data: canvaStatus } = useCanvaQuery();
	const canvaSessionExpired =
		canvaStatus?.connected === true && canvaStatus.tokenExpired === true;

	const [assetFilter, setAssetFilter] = useState<AssetFilter>("all");
	const [dateFilter, setDateFilter] = useState<CanvaDateFilter>("all");
	const [sortBy, setSortBy] = useState<CanvaDesignSortBy>(
		"modified_descending",
	);
	const [searchWord, setSearchWord] = useState("");
	const [debouncedSearch] = useDebounceValue(searchWord, 400);
	const [canvaFolderId, setCanvaFolderId] = useState(CANVA_ROOT_FOLDER_ID);
	const [folderPath, setFolderPath] = useState<CanvaFolderBreadcrumb[]>([
		{ id: CANVA_ROOT_FOLDER_ID, name: "Projects" },
	]);
	const [folders, setFolders] = useState<CanvaFolderDto[]>([]);
	const [continuation, setContinuation] = useState<string | null>(null);
	const [designs, setDesigns] = useState<CanvaDesignDto[]>([]);
	const [selected, setSelected] = useState<Map<string, CanvaDesignDto>>(
		() => new Map(),
	);
	const [importId, setImportId] = useState<number | null>(null);
	const [retryingId, setRetryingId] = useState<number | null>(null);

	const assetTypeParam =
		assetFilter === "all" ? undefined : (assetFilter as CanvaAssetType);
	const isSearching = debouncedSearch.length > 0;

	const resetBrowseState = useCallback(() => {
		setContinuation(null);
		setDesigns([]);
		setFolders([]);
		setSelected(new Map());
	}, []);

	const handleAssetFilterChange = useCallback(
		(filter: AssetFilter) => {
			setAssetFilter(filter);
			resetBrowseState();
		},
		[resetBrowseState],
	);

	const handleDateFilterChange = useCallback(
		(filter: CanvaDateFilter) => {
			setDateFilter(filter);
			resetBrowseState();
		},
		[resetBrowseState],
	);

	const handleSortChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			setSortBy(event.target.value as CanvaDesignSortBy);
			resetBrowseState();
		},
		[resetBrowseState],
	);

	const handleOpenFolder = useCallback(
		(folderId: string, folderName?: string) => {
			setCanvaFolderId(folderId);
			resetBrowseState();
			if (folderId === CANVA_ROOT_FOLDER_ID) {
				setFolderPath([{ id: CANVA_ROOT_FOLDER_ID, name: "Projects" }]);
				return;
			}
			setFolderPath((prev) => {
				const root = prev[0] ?? {
					id: CANVA_ROOT_FOLDER_ID,
					name: "Projects",
				};
				if (folderName) {
					return [root, { id: folderId, name: folderName }];
				}
				return [...prev, { id: folderId, name: "Folder" }];
			});
		},
		[resetBrowseState],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset results when the debounced query changes
	useEffect(() => {
		resetBrowseState();
	}, [debouncedSearch, resetBrowseState]);

	const designsQuery = useQuery({
		queryKey: QueryKeys.Integrations.Canva.Designs(
			companyId,
			assetFilter,
			continuation,
			debouncedSearch,
			sortBy,
			dateFilter,
			isSearching ? CANVA_ROOT_FOLDER_ID : canvaFolderId,
		),
		queryFn: async () => {
			const res = await Api.CanvaListDesigns({
				continuation: continuation ?? undefined,
				limit: 20,
				assetType: assetTypeParam,
				query: debouncedSearch || undefined,
				sortBy,
				dateFilter,
				canvaFolderId: isSearching ? undefined : canvaFolderId,
			});
			return res.data;
		},
		enabled:
			(canvaStatus?.connected ?? false) &&
			!canvaSessionExpired &&
			importId === null,
	});

	const designsAuthError =
		designsQuery.isError && isCanvaAuthError(designsQuery.error);
	const needsReconnect = canvaSessionExpired || designsAuthError;

	useEffect(() => {
		if (!designsAuthError) {
			return;
		}
		void queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.Canva.Status(companyId),
		});
	}, [designsAuthError, companyId, queryClient]);

	useEffect(() => {
		if (!designsQuery.data || designsQuery.isFetching) {
			return;
		}
		setDesigns((prev) => {
			if (!continuation) {
				return designsQuery.data.items;
			}
			const existing = new Set(prev.map((d) => d.id));
			const merged = [...prev];
			for (const item of designsQuery.data.items) {
				if (!existing.has(item.id)) {
					merged.push(item);
				}
			}
			return merged;
		});
		if (!continuation && designsQuery.data.folders.length > 0) {
			setFolders(designsQuery.data.folders);
		}
		if (!continuation && designsQuery.data.folderPath.length > 0) {
			setFolderPath(designsQuery.data.folderPath);
		}
	}, [designsQuery.data, designsQuery.isFetching, continuation]);

	const nextContinuation = designsQuery.data?.continuation ?? null;
	const isLoadingDesigns =
		designs.length === 0 &&
		folders.length === 0 &&
		(designsQuery.isLoading || designsQuery.isFetching);

	const importStatusQuery = useQuery({
		queryKey: QueryKeys.Integrations.Canva.Import(companyId, importId ?? 0),
		queryFn: async () => {
			const res = await Api.CanvaImportStatus(importId as number);
			return res.data;
		},
		enabled: importId !== null,
		staleTime: 0,
	});

	useCanvaImportStream(companyId, importId);

	useEffect(() => {
		const data = importStatusQuery.data;
		if (!data) return;
		if (data.status === "completed" || data.status === "failed") {
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.All(companyId),
			});
		}
	}, [importStatusQuery.data, companyId, queryClient]);

	const startImport = useMutation({
		mutationFn: async () => {
			const designsToImport = Array.from(selected.values()).slice(
				0,
				CANVA_MAX_BATCH_SIZE,
			);
			const res = await Api.CanvaStartImport({
				designs: designsToImport.map((d) => ({
					designId: d.id,
					title: d.title,
					assetType: d.assetType,
				})),
				folderId: props.folderId ?? null,
			});
			return res.data;
		},
		onSuccess: (data) => {
			setImportId(data.id);
			emit({
				type: AlertType.SUCCESS,
				content: `Importing ${data.totalAssets} design${data.totalAssets === 1 ? "" : "s"} from Canva`,
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to start Canva import. Please try again.",
			});
		},
	});

	const toggleDesign = useCallback((design: CanvaDesignDto) => {
		setSelected((prev) => {
			const next = new Map(prev);
			if (next.has(design.id)) {
				next.delete(design.id);
			} else if (next.size < CANVA_MAX_BATCH_SIZE) {
				next.set(design.id, design);
			}
			return next;
		});
	}, []);

	const handleRetry = useCallback(
		async (item: CanvaImportItemDto) => {
			setRetryingId(item.id);
			try {
				await Api.CanvaRetryImportItem(item.id, props.folderId ?? null);
				void importStatusQuery.refetch();
			} catch {
				emit({
					type: AlertType.ERROR,
					content: "Retry failed. Please try again.",
				});
			} finally {
				setRetryingId(null);
			}
		},
		[props.folderId, importStatusQuery, emit],
	);

	const selectedCount = selected.size;
	const importDone =
		importStatusQuery.data?.status === "completed" ||
		importStatusQuery.data?.status === "failed";

	const emptyMessage = useMemo(() => {
		if (isSearching) {
			return `No designs matching "${debouncedSearch}".`;
		}
		if (assetFilter === "video") {
			return "No video designs found. Only designs that import as MP4 appear here.";
		}
		if (assetFilter === "animation") {
			return "No animation designs found. Only designs that import as GIF appear here.";
		}
		if (assetFilter === "image") {
			return "No image designs found. Only designs that import as PNG appear here.";
		}
		return "No designs found in this Canva folder.";
	}, [assetFilter, debouncedSearch, isSearching]);

	const showBrowse = importId === null || !importStatusQuery.data;

	return (
		<Dialog className="canva-import-modal">
			<Dialog.Header title="Import from Canva" />
			<Dialog.Body>
				<p className="canva-powered-by">
					<span className="canva-powered-by__logo">
						<Icon name={IconSVG.Canva} size={24} />
					</span>
					Powered by Canva
				</p>
				<p className="canva-import-subtitle">
					Select designs to import into your media library. Up to{" "}
					{CANVA_MAX_BATCH_SIZE} per batch. Already-imported designs can be
					re-imported to get the latest version.
					{selectedCount > 0 && (
						<span className="canva-import-selected-count">
							{" "}
							· {selectedCount} selected
						</span>
					)}
				</p>

				{showBrowse ? (
					needsReconnect ? (
						<div className="canva-import-reconnect">
							<p className="text-center text-muted mb-0">
								Your Canva session has expired. Reconnect to browse and import
								designs.
							</p>
							<NavLink to={LinkGen.Settings.Apps()}>
								<Button>Reconnect Canva</Button>
							</NavLink>
						</div>
					) : (
						<>
							<div className="canva-import-toolbar">
								<ExtraForm.Control
									value={searchWord}
									onChange={(e) => setSearchWord(e.target.value)}
									placeholder="Search designs by name"
									type="search"
									aria-label="Search Canva designs"
								/>
								<ExtraForm.Select
									value={sortBy}
									onChange={handleSortChange}
									aria-label="Sort designs"
								>
									{SORT_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</ExtraForm.Select>
							</div>

							<SegmentedControl
								className="canva-import-filters"
								options={ASSET_FILTERS}
								value={assetFilter}
								onChange={handleAssetFilterChange}
								ariaLabel="Filter by asset type"
							/>

							<SegmentedControl
								className="canva-import-filters"
								options={DATE_FILTERS}
								value={dateFilter}
								onChange={handleDateFilterChange}
								ariaLabel="Filter by date"
							/>

							{!isSearching && (
								<FolderBreadcrumb
									path={folderPath}
									onNavigate={(folderId) => {
										const crumb = folderPath.find(
											(item) => item.id === folderId,
										);
										handleOpenFolder(folderId, crumb?.name);
									}}
								/>
							)}

							<div className="canva-import-content">
								{isLoadingDesigns ? (
									<div className="canva-import-loading">
										<Spinner />
									</div>
								) : (
									<div className="canva-design-grid">
										{!isSearching &&
											folders.map((folder) => (
												<FolderCard
													key={folder.id}
													folder={folder}
													onOpen={(folderId) =>
														handleOpenFolder(folderId, folder.name)
													}
												/>
											))}
										{designs.map((design) => (
											<DesignCard
												key={design.id}
												design={design}
												selected={selected.has(design.id)}
												onToggle={toggleDesign}
											/>
										))}
									</div>
								)}

								{designs.length === 0 &&
									folders.length === 0 &&
									!isLoadingDesigns &&
									!designsQuery.isError && (
										<p className="canva-import-empty">{emptyMessage}</p>
									)}

								{designsQuery.isError && !designsAuthError && (
									<p className="canva-import-empty">
										Could not load Canva designs. Please try again.
									</p>
								)}

								{nextContinuation && (
									<div className="canva-import-load-more">
										<Button
											variant="secondary"
											size="sm"
											loading={designsQuery.isFetching}
											onClick={() => setContinuation(nextContinuation)}
										>
											Load more
										</Button>
									</div>
								)}
							</div>
						</>
					)
				) : (
					<ImportProgressPanel
						status={importStatusQuery.data}
						onRetry={handleRetry}
						retryingId={retryingId}
					/>
				)}
			</Dialog.Body>

			<Dialog.Footer className="actions">
				{showBrowse ? (
					needsReconnect ? (
						<Button variant="secondary" onClick={() => dialog.close()}>
							Close
						</Button>
					) : (
						<>
							<Button variant="secondary" onClick={() => dialog.close()}>
								Cancel
							</Button>
							<Button
								onClick={() => startImport.mutate()}
								loading={startImport.isPending}
								disabled={selectedCount === 0}
							>
								Import{selectedCount > 0 ? ` ${selectedCount}` : ""}
							</Button>
						</>
					)
				) : importDone ? (
					<Button onClick={() => dialog.close()}>Done</Button>
				) : (
					<Button variant="secondary" onClick={() => dialog.close()}>
						Import in background
					</Button>
				)}
			</Dialog.Footer>
		</Dialog>
	);
}
